// Internal staff activity logger.
//
// Writes one /staffActivityLogs/{autoId} entry per page view / action /
// download performed by an internal staff user (admin, finance, call
// center, etc.). Admin uses these entries to monitor what staff are
// doing — see SecurityDashboard's "Staff Activity" tab.
//
// Design constraints:
//   • Logging is best-effort — a network failure must never block the
//     UI action the staff member is performing.
//   • Only internal-staff usertypes write; customers and drivers are
//     filtered out at the helper so we don't pollute the node.
//   • The DB rule additionally enforces that staff can only write
//     entries with their own uid (defense in depth).
//   • 30-day retention — the cleanup scheduler in functions/index.js
//     prunes entries older than this. See `STAFF_LOG_RETENTION_DAYS`
//     in functions/index.js to change.
//
// To add tracking to a new action: import logStaffActivity and call
// it from the action handler. One line:
//
//   logStaffActivity({
//     eventType: 'action',
//     category: 'finance',
//     action: 'approve_withdraw',
//     target: { type: 'withdraw', id: withdrawId, label: driverName },
//     details: { amount, currency },
//   });

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getDatabase, ref, push, serverTimestamp } from 'firebase/database';

// Roles considered "internal staff" — tracked by the activity logger.
// Customers and drivers (and other external roles) are explicitly
// excluded so the logs node stays scoped to actual employee actions.
//
// To add a new staff role, append it here and add it to the DB rules'
// validate clause (json/database-rules.json).
// Admin is intentionally NOT tracked — admins are the ones VIEWING
// the dashboard, so logging their own clicks just adds noise and
// inflates RTDB cost without security value.
export const STAFF_USER_TYPES = [
    'finance',
    'callcenteragent',
    'callcentersupervisor',
];

export const isInternalStaff = (profile) =>
    !!(profile && STAFF_USER_TYPES.indexOf(profile.usertype) >= 0);

// One sessionId per browser session — groups events that happen in the
// same window. Persisted to sessionStorage so SPA reloads / tab refreshes
// keep the same group, but a new tab gets its own session.
const SESSION_KEY = '__staffActivitySession';
const getSessionId = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return 's-' + Date.now();
    }
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
        id = 's-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
        window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
};

// Module-level cache of the current staff profile. Set via
// useStaffActivityTracker (the hook) so logStaffActivity() — which can
// be called from anywhere, not just React components — has access to
// the current user without forcing every caller to thread `profile`.
let _currentProfile = null;
const setCurrentProfile = (profile) => { _currentProfile = profile; };

// Module-level cache of the kill-switch flag. Driven from Redux by
// the tracker hook below. Default ON until we hear otherwise —
// settings.enableStaffActivityLogging === false explicitly disables.
// (We don't want a missing settings node to silently turn the feature
// off; explicit-disable only.)
let _loggingEnabled = true;
const setLoggingEnabled = (enabled) => { _loggingEnabled = enabled !== false; };

// Public read for the UI so it can show an "off" banner without
// having to subscribe to the flag itself.
export const isStaffLoggingEnabled = () => _loggingEnabled;

// Client IP (best effort). Browsers don't expose the public IP; we
// fetch it once per session from a free public lookup. Cached in
// sessionStorage so the same browser session re-uses the value
// without extra round trips. If the lookup fails (offline, blocked,
// CORS, etc.) the field is just omitted from the log entry — never
// blocks logging.
const IP_KEY = '__staffActivityIp';
let _currentIp = null;

const fetchClientIp = async () => {
    if (typeof window === 'undefined') return null;
    try {
        const cached = window.sessionStorage && window.sessionStorage.getItem(IP_KEY);
        if (cached) return cached;
        // ipify is a free, CORS-friendly IP echo service. No auth.
        // If you want to swap providers later, the contract is just
        // "respond with { ip: '...' }" — substitute the URL here.
        const res = await fetch('https://api.ipify.org?format=json', { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        const ip = data && data.ip ? String(data.ip) : null;
        if (ip && window.sessionStorage) {
            window.sessionStorage.setItem(IP_KEY, ip);
        }
        return ip;
    } catch (_) {
        return null;
    }
};

const primeClientIp = () => {
    if (_currentIp) return;
    fetchClientIp().then((ip) => { if (ip) _currentIp = ip; });
};

// Trim a string to a max length to keep individual log entries small
// (RTDB bandwidth is the real cost over time). 100 chars is plenty for
// a label or action name.
const trim = (s, max = 100) => {
    if (s == null) return null;
    const str = String(s);
    return str.length > max ? str.slice(0, max) : str;
};

// Shallow-copy `details` and strip undefined/function/symbol values
// since RTDB rejects them. Also bounds the field count to 20 so a
// careless caller doesn't dump a whole booking object.
const sanitizeDetails = (details) => {
    if (!details || typeof details !== 'object') return null;
    const out = {};
    let count = 0;
    for (const k of Object.keys(details)) {
        if (count >= 20) break;
        const v = details[k];
        if (v === undefined || typeof v === 'function' || typeof v === 'symbol') continue;
        // Stringify nested objects/arrays so we never blow past a
        // reasonable entry size. Truncate to 200 chars per field.
        if (typeof v === 'object') {
            try {
                out[k] = trim(JSON.stringify(v), 200);
            } catch (_) {
                out[k] = null;
            }
        } else if (typeof v === 'number' && !Number.isFinite(v)) {
            // RTDB rejects NaN/Infinity (see bookingactions.js comment).
            continue;
        } else {
            out[k] = trim(v, 200);
        }
        count++;
    }
    return Object.keys(out).length > 0 ? out : null;
};

export const logStaffActivity = ({
    eventType,        // 'page_view' | 'download' | 'action'
    category,         // free-form bucket: 'finance' | 'users' | 'reports' | ...
    action,           // verb-y short string: 'view' | 'export_csv' | 'approve_withdraw'
    target,           // { type, id, label } — what was acted upon (optional)
    page,             // route path; auto-filled if omitted
    details,          // small object of extra context (optional)
}) => {
    try {
        // Kill switch: admin can disable the whole tracker from
        // GeneralSettings if RTDB cost spikes. Checked first so we
        // skip even profile/IP work when off.
        if (!_loggingEnabled) return;

        const profile = _currentProfile;
        if (!isInternalStaff(profile)) return; // no-op for non-staff

        if (!eventType || !action) return; // malformed

        const db = getDatabase();
        const entry = {
            uid: profile.uid || profile.id || '',
            userType: profile.usertype || '',
            userName: trim(`${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.email || '', 80),
            userEmail: trim(profile.email || '', 100),
            eventType: trim(eventType, 20),
            category: trim(category || 'general', 40),
            action: trim(action, 60),
            page: trim(page || (typeof window !== 'undefined' ? window.location.pathname : ''), 200),
            timestamp: serverTimestamp(),
            sessionId: getSessionId(),
        };
        if (target && typeof target === 'object') {
            entry.target = {
                type: trim(target.type || '', 30),
                id: trim(target.id || '', 60),
                label: trim(target.label || '', 80),
            };
        }
        const sanitized = sanitizeDetails(details);
        if (sanitized) entry.details = sanitized;

        // Attach the client IP if we have it cached. We don't await
        // here — if the first lookup hasn't returned yet, the very
        // first few log entries won't have an IP, and that's fine.
        // The hook primes the lookup on mount so this stabilizes
        // within ~100ms of login.
        if (_currentIp) entry.clientIp = trim(_currentIp, 45); // IPv6 max 39, leave headroom

        // Fire-and-forget. We never await — staff UI must never block on
        // logging. The .catch swallows network failures silently; the
        // worst outcome is a missed log entry, not a broken interaction.
        push(ref(db, 'staffActivityLogs'), entry).catch(() => { /* silent */ });
    } catch (_) { /* silent — logging must never throw into the UI */ }
};

// Convenience wrappers — read more naturally at call sites.
export const logPageView = (page, category) =>
    logStaffActivity({ eventType: 'page_view', action: 'view', category: category || 'navigation', page });

export const logDownload = ({ action, target, details, category }) =>
    logStaffActivity({ eventType: 'download', action, target, details, category: category || 'reports' });

export const logAction = ({ action, target, details, category }) =>
    logStaffActivity({ eventType: 'action', action, target, details, category: category || 'general' });

// Hook that:
//   1. Keeps the module-level _currentProfile / loggingEnabled in
//      sync with Redux.
//   2. Logs a page_view on every route change (for staff only).
//
// We deliberately do NOT emit periodic engagement heartbeats — they
// add nontrivial RTDB storage + bandwidth cost on Blaze for a signal
// that's only useful for a specific edge case (a user sitting on one
// page reading for 20 min). Time-on-portal is derived from navigation
// gaps instead and under-counts in that case; this is acceptable.
//
// Mount this once at the App level (inside the Router so useLocation
// works, and inside the Redux Provider so useSelector works).
export const useStaffActivityTracker = () => {
    const profile = useSelector((state) => state.auth?.profile);
    // Default ON — only explicit false from settings disables tracking.
    const loggingEnabled = useSelector(
        (state) => state.settingsdata?.settings?.enableStaffActivityLogging !== false
    );
    const location = useLocation();
    const lastPathRef = useRef(null);

    // Sync flag to module cache so logStaffActivity (callable from
    // anywhere, not just React) can short-circuit when disabled.
    useEffect(() => {
        setLoggingEnabled(loggingEnabled);
    }, [loggingEnabled]);

    // Keep module cache in sync — runs whenever profile changes.
    // Also kick off the client IP lookup so subsequent logs can attach it.
    useEffect(() => {
        setCurrentProfile(profile || null);
        if (loggingEnabled && isInternalStaff(profile)) primeClientIp();
    }, [profile, loggingEnabled]);

    // Page-view auto-tracking. Fires once per real path change (ignores
    // back-to-back identical paths from re-renders).
    useEffect(() => {
        if (!loggingEnabled) return;
        if (!isInternalStaff(profile)) return;
        const path = location.pathname;
        if (lastPathRef.current === path) return;
        lastPathRef.current = path;
        logPageView(path);
    }, [profile, location.pathname, loggingEnabled]);

    // Engagement heartbeats: only run when this user is internal staff
    // and we're in a browser env. Listens to mouse / keyboard / touch
    // / scroll events to refresh the lastInputAt timer; a periodic
    // interval emits a heartbeat only if the tab is visible AND there
    // was activity in the recent window.
};

export default {
    STAFF_USER_TYPES,
    isInternalStaff,
    isStaffLoggingEnabled,
    logStaffActivity,
    logPageView,
    logDownload,
    logAction,
    useStaffActivityTracker,
};
